import { useCallback, useEffect, useState } from 'react';
import { messageOf } from '../utils/rfqErrors';

export interface AsyncData<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Reads whatever `loader` returns, again whenever `loader` changes identity or `reload` is called.
 *
 * The caller memoises the loader with `useCallback` over the query it depends on, so a new query is a new
 * loader and a stale answer is dropped rather than drawn over a newer one. `null` loads nothing.
 */
export function useAsyncData<T>(loader: (() => Promise<T>) | null): AsyncData<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(loader !== null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!loader) {
      setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    loader()
      .then((result) => {
        if (!live) return;
        setData(result);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (!live) return;
        setError(messageOf(reason));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [loader, tick]);

  const reload = useCallback(() => {
    setTick((value) => value + 1);
  }, []);

  return { data, loading, error, reload };
}
